import { FC, useContext } from "react";
import { Controller } from "react-hook-form";
import { AsyncSelect, Form, FormField, ModalDialog, Numeric } from "@rothoblaas/design-system";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useAsyncOptions from "../../../../hooks/useAsyncOptions";
import useI18n from "../../../../hooks/useTranslations";
import { ScrewFamily } from "../../../../http/deckDesigner/types/screwFamilies";
import { InsertScrewBody, Screw, UpdateScrewBody } from "../../../../http/deckDesigner/types/screws";
import useDeckScrewFamiliesApi from "../../../../http/deckDesigner/useScrewFamilies";
import useDeckScrewsApi from "../../../../http/deckDesigner/useScrews";
import useItemOptions, { getItemOptionLabel, ItemOption, toItemOption } from "../../../../hooks/useItemOptions";

const MIN_MEASUREMENT_MM = 0.001;

interface ScrewFormBody {
  item: ItemOption | null;
  screwFamily: ScrewFamily | null;
  length: number | null;
  screwHeadHeight: number | null;
  screwDiameter: number | null;
}

interface ScrewFormProps {
  screw?: Screw;
  onClose: () => void;
}

const ScrewForm: FC<ScrewFormProps> = ({ screw, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const screwsApi = useDeckScrewsApi();
  const screwFamiliesApi = useDeckScrewFamiliesApi();
  const itemOptions = useItemOptions();
  const screwFamilyOptions = useAsyncOptions(screwFamiliesApi.getDeckScrewFamilies);

  const defaultValues: ScrewFormBody = {
    item: screw ? toItemOption(screw.item) : null,
    screwFamily: screw?.screwFamily ?? null,
    length: screw?.length ?? null,
    screwHeadHeight: screw?.screwHeadHeight ?? null,
    screwDiameter: screw?.screwDiameter ?? null,
  };

  const onSubmit = () => async (formData: ScrewFormBody) => {
    if (
      !formData.item ||
      !formData.screwFamily ||
      formData.length == null ||
      formData.screwHeadHeight == null ||
      formData.screwDiameter == null
    ) {
      return;
    }

    const body: InsertScrewBody | UpdateScrewBody = {
      itemId: formData.item.id,
      screwFamilyId: formData.screwFamily.id,
      length: formData.length,
      screwHeadHeight: formData.screwHeadHeight,
      screwDiameter: formData.screwDiameter,
    };

    try {
      if (screw) {
        await screwsApi.updateDeckScrew(screw.item.id, body);
      } else {
        await screwsApi.insertDeckScrew(body);
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        screw
          ? `${i18n.translation.common.edit} ${i18n.translation.deckDesignerModule.screws.singular}`
          : `${i18n.translation.common.add} ${i18n.translation.deckDesignerModule.screws.singular}`
      }
    >
      <Form<ScrewFormBody> submitText={i18n.translation.common.save} onSubmit={onSubmit} defaultValues={defaultValues}>
        {({ control }) => (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                  {({ labelId }) => (
                    <AsyncSelect<ItemOption, false>
                      {...field}
                      {...itemOptions}
                      inputId={labelId}
                      isMulti={false}
                      isDisabled={!!screw}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={getItemOptionLabel}
                      getOptionValue={(option) => option.id}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="screwFamily"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.deckDesignerModule.screwFamilies.singular} error={fieldState.error}>
                  {({ labelId }) => (
                    <AsyncSelect<ScrewFamily, false>
                      {...field}
                      {...screwFamilyOptions}
                      inputId={labelId}
                      isMulti={false}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.id}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="length"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.length} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      min={1}
                      decimalScale={0}
                      suffix={" " + i18n.translation.common.unitMm}
                      allowNegative={false}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="screwHeadHeight"
              control={control}
              rules={{ required: true, min: MIN_MEASUREMENT_MM }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.screwHeadHeight} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      min={MIN_MEASUREMENT_MM}
                      decimalScale={3}
                      suffix={" " + i18n.translation.common.unitMm}
                      allowNegative={false}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="screwDiameter"
              control={control}
              rules={{ required: true, min: MIN_MEASUREMENT_MM }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.screwDiameter} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      min={MIN_MEASUREMENT_MM}
                      decimalScale={3}
                      suffix={" " + i18n.translation.common.unitMm}
                      allowNegative={false}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default ScrewForm;
